import safeGetAttribute from './safeGetAttribute';
import { INLINEIMAGE_ATTRIBUTE_ORIGINALSRC } from 'owa-inline-image-consts';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';
import { format } from 'owa-string-utils';
import calculateImageProxyPath from './calculateImageProxyPath';

const IMAGE_PROXY_FORMAT_URL = 'https://imgproxy.services.web.outlook.com/proxy/?u={0}&t={1}';

// The resource url for model D image proxy.
// TODO: we will need to update it to match with IMAGE_PROXY_URL where it is really served
const IMAGE_PROXY_RESOURCE_URL = 'https://outlookimageproxy.azurewebsites.net';

// We want to capture perf for both model B and D for latency comparision
const MODELB_DATAPOINT_NAME = 'ImageProxyModelB';
const MODELD_DATAPOINT_NAME = 'ImageProxyModelD';

export default async function loadFromModelDProxy(img: HTMLImageElement, shadowTraffic: boolean) {
    // This is img that will be used for loading model-D
    // In non-shadowing, it is the real img in DOM. In shadowing, it will be temp un-attached img
    let modelDImage = img;

    // Important things first. On shadowing, the image is still loaded through the original Model-B service
    // Do that first, and then do the shadowing after
    let modelBSuccess = false;
    if (shadowTraffic) {
        const modelBDatapoint = new PerformanceDatapoint(MODELB_DATAPOINT_NAME);
        const modelBProxySrc = await calculateImageProxyPath(img);
        if (!modelBProxySrc) {
            return;
        }

        // The Model-B
        if (await loadImage(img, modelBProxySrc)) {
            modelBDatapoint.end();
            modelBSuccess = true;
        } else {
            modelBDatapoint.endWithError(DatapointStatus.ServerError);
        }

        // Shadowing, use temp img for modelDImage
        modelDImage = document.createElement('img');
    }

    const originalSrc = safeGetAttribute(img, INLINEIMAGE_ATTRIBUTE_ORIGINALSRC);
    if (!originalSrc) {
        // Model-D can't work without originalSrc
        return;
    }

    const modelDDatapoint = new PerformanceDatapoint(MODELD_DATAPOINT_NAME);
    let [token, tokenPromise] = getAccessTokenforResourceAsLazy(IMAGE_PROXY_RESOURCE_URL);
    if (!token) {
        token = await tokenPromise;
    }

    let modelDSuccess = false;
    let tokenLength = token ? token.length : 0;

    // Only attempt to load when we have a token
    if (tokenLength > 0) {
        const proxySrc = format(
            IMAGE_PROXY_FORMAT_URL,
            encodeURIComponent(originalSrc),
            encodeURIComponent(token)
        );

        modelDSuccess = await loadImage(modelDImage, proxySrc);
    }

    if (shadowTraffic) {
        modelDDatapoint.addCustomData({
            resultB: modelBSuccess,
            resultD: modelDSuccess,
            tokenLen: tokenLength,
        });
    }

    modelDSuccess
        ? modelDDatapoint.end()
        : modelDDatapoint.endWithError(
              tokenLength == 0 ? DatapointStatus.ClientError : DatapointStatus.ServerError
          );
}

function loadImage(img: HTMLImageElement, src: string): Promise<boolean> {
    return new Promise<boolean>(resolve => {
        img.onload = e => {
            removeImageEventHandlers(img);
            resolve(true);
        };

        img.onerror = e => {
            removeImageEventHandlers(img);
            resolve(false);
        };

        img.src = src;
    });
}

function removeImageEventHandlers(img: HTMLImageElement) {
    img.onload = null;
    img.onerror = null;
}
